import { type ComponentProps, type FC, memo, useCallback } from "react";
import { IconButton, ButtonKind, ButtonSize, ButtonVariant } from "@stubhub/broadway-web";
import type { FlagCountry } from "@stubhub/broadway-web/icons/flags";
import { NotificationIcon } from "@stubhub/broadway-web/icons/functional";
import {
  EventGridListItem,
  EventGridListItemActions,
  EventGridListItemBody,
  EventGridListItemCalendar,
  EventGridListItemCtas,
  EventGridListItemEventState,
  EventGridListItemEventType,
  EventGridListItemKind,
  EventGridListItemVariant,
  eventGridListItemCtasRecipe,
} from "@stubhub/ensemble-discovery";
import {
  Feature,
  RecordParticipationTypes,
  useFeatureParticipationRecorder,
  useSimpleFeatureTernary,
} from "@viagogo/feature";
import { EventState } from "src/http/api-types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { DataTestIds } from "src/shared/constants/TestEnums";
import { gaCardBodyCenterStyle } from "src/website-apps/responsive/stubhub-performer/components/ga-page/styles";
import { PerformerEventGridListItemBodyContent } from "src/website-apps/responsive/stubhub-performer/components/performer-event-grid-list-item/performer-event-grid-list-item-body-content";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import type { CommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/common-event-grid-list-item";
import { useCommonEventGridListItem } from "src/website-apps/shared/components/common-event-grid-list-item/use-common-event-grid-list-item";
import { NotificationFillIcon } from "src/website-apps/shared/components/FavoritesV2Buttons/FavoritesV2Button/icons/notification-fill";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { NotificationModal } from "src/website-apps/shared/components/on-sale-notifications/NotificationModal";

/**
 * Simplified event grid list item for GA (General Admission) performer pages.
 *
 * Follows the same signature as CommonEventGridListItem so it can be used as
 * an `EventGridListItemComponent` in FiltersAndGridsManager.
 *
 * Compared to PerformerEventGridListItem this strips out:
 * - Sports logic (team vs team, playoffs, season tickets)
 * - Residency / grouped events (showtimes)
 * - Custom hover tracking / venue prefetch experiments
 *
 * Pass-type / weekend filtering is handled by GaFestivalRenderer in
 * PerformerEventGridSurface.tsx — it filters the grid items before they
 * reach this component, so each card only renders visible events.
 */
export const GAEventGridListItem: FC<ComponentProps<typeof CommonEventGridListItem>> = memo(
  ({ index, event, performerName, searchKeyWord, onClick, gridType, previewImageUrl }) => {
    // Use the backend-computed tier from GaPassClassification instead of
    // client-side string splitting — tier is locale-aware and handles
    // naming variations the frontend can't predict.
    const surface = usePerformerPageModelData().performerGridSurface;
    const classification =
      surface?.type === "ga-festival" ? surface.gaPassClassifications[String(event.eventId)] : undefined;
    // displayName from the backend is fully locale-formatted: weekday names,
    // "Festival Pass", and sub-event prefixes are all resolved server-side.
    const passName = classification?.displayName ?? classification?.tier ?? event.name;

    const {
      appContext,
      isStubhub,
      isFavorited,
      isNotificationStored,
      favoritesEnabled,
      willRedirectToStubhubUk,
      eventUrl,
      eventState,
      venueLocation,
      onSaleTimeMillis,
      flagCountries,
      defaultTags,
      merchTags,
      merchInViewTrackers,
      handleOnClick,
      handleOnInView,
      handleFollow,
      isNotificationModalOpen,
      handleCloseNotificationModal,
    } = useCommonEventGridListItem({
      event,
      gridType,
      index,
      onClick,
      previewSummary: { name: event.name, imageUrl: previewImageUrl ?? null },
    });

    const showFollow = favoritesEnabled && isStubhub && !event.isParkingEvent && !willRedirectToStubhubUk;

    // CWP-1929: icon-only notify pill on tablet/desktop — eligible on follow-eligible sold-out/coming-soon tiles.
    const canParticipateNotifyIconCta = useCallback(
      () =>
        showFollow &&
        (eventState === EventGridListItemEventState.SoldOut || eventState === EventGridListItemEventState.ComingSoon),
      [showFollow, eventState],
    );
    const showNotifyIcon =
      useSimpleFeatureTernary(
        FeatureSetEnum.TPerformerNotifyIconCta,
        false,
        true,
        canParticipateNotifyIconCta,
        false,
      ) ?? false;
    const recordNotifyIconCtaInView = useFeatureParticipationRecorder(
      FeatureSetEnum.TPerformerNotifyIconCta,
      canParticipateNotifyIconCta,
    );

    return (
      <li key={event.eventId} data-testid={DataTestIds.GaEventGridListItem}>
        <InViewTracker
          onInView={() => {
            handleOnInView();
            recordNotifyIconCtaInView();
          }}
        />
        <EventGridListItem
          href={eventUrl ?? "#"}
          onClick={handleOnClick}
          kind={EventGridListItemKind.Neutral}
          variant={EventGridListItemVariant.Outline}
          searchQuery={searchKeyWord ?? undefined}
          eventState={eventState}
          eventType={event.isParkingEvent ? EventGridListItemEventType.Parking : EventGridListItemEventType.Event}
          eventTitle={passName}
          venueName={event.venueName ?? ""}
          venueLocation={venueLocation}
          venueCountryCode={event.countryCode as FlagCountry}
          startTimeMillis={event.eventMetadata?.common?.eventStartDateTime}
          endTimeMillis={event.eventMetadata?.common?.eventEndDateTime ?? undefined}
          venueTimeZoneOffsetMillis={event.eventMetadata?.common?.venueTimeZoneOffset}
          onSaleTimeMillis={onSaleTimeMillis}
          passLength={event.eventMetadata?.common?.passLength ?? undefined}
          isDateConfirmed={event.isDateConfirmed}
          isTimeConfirmed={event.isTimeConfirmed}
          isPostponed={event.eventState === EventState.Postponed}
        >
          <EventGridListItemCalendar />
          <EventGridListItemBody flagCountries={flagCountries} classes={{ container: gaCardBodyCenterStyle }}>
            {merchInViewTrackers}
            <PerformerEventGridListItemBodyContent
              event={event}
              isNBA={false}
              isNFL={false}
              isNHL={false}
              isMLB={false}
              isNationalTeam={false}
              locationPriority={false}
              subtitleMode="ga"
              hideMerchTags={willRedirectToStubhubUk}
              tags={defaultTags}
              merchTags={merchTags}
              isPlayoffEvent={false}
            />
          </EventGridListItemBody>
          <EventGridListItemActions>
            <Feature
              id={FeatureSetEnum.TMPerformerReplaceHeartWithNotificationIcon}
              recordType={RecordParticipationTypes.IN_VIEW}
              canParticipate={() => showFollow && eventState === EventGridListItemEventState.Active}
              control={() => (
                <EventGridListItemCtas
                  isFollowed={isFavorited}
                  onFollowClick={handleFollow}
                  showFollow={showFollow}
                  showNotify={showFollow}
                  showNotifyIcon={showNotifyIcon}
                  isNotified={isNotificationStored}
                />
              )}
              variant={() => (
                <>
                  <EventGridListItemCtas
                    isFollowed={isFavorited}
                    onFollowClick={handleFollow}
                    showFollow={false}
                    showNotify={showFollow}
                    showNotifyIcon={showNotifyIcon}
                    isNotified={isNotificationStored}
                  />
                  {showFollow && (
                    <IconButton
                      aria-label={isFavorited ? "Unfollow" : "Follow"}
                      classes={{ root: eventGridListItemCtasRecipe().buttonHiddenMobileRoot ?? "" }}
                      icon={isFavorited ? NotificationFillIcon : NotificationIcon}
                      kind={ButtonKind.Neutral}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleFollow(e);
                      }}
                      size={ButtonSize.Small}
                      variant={ButtonVariant.Outline}
                    />
                  )}
                </>
              )}
            />
          </EventGridListItemActions>
        </EventGridListItem>
        <NotificationModal
          isOpen={isNotificationModalOpen}
          onClose={handleCloseNotificationModal}
          userFirstName={appContext.userFirstName ?? ""}
          userEmailAddress={appContext.userEmailAddress ?? ""}
          performerName={performerName}
          entityId={event.eventId}
          notificationModalContent={event.notificationModalContent}
          shouldEnableAdjustLinks
        />
      </li>
    );
  },
);
GAEventGridListItem.displayName = "GAEventGridListItem";
